import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  Suspense,
  lazy,
} from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchUser } from "@/modules/auth/store/authSlice";
import {
  addGlobalAlert,
  removeGlobalAlert,
  setLoading,
  setProgress,
} from "@/modules/shared/store/uiSlice";
import "./App.css";

import Navbar from "./components/Navbar";
import PrivateRoute from "./components/PrivateRoute";
import { logout as logoutThunk } from "@/modules/auth/store/authSlice";
import SkeletonLoader from "./components/SkeletonLoader";
import GlobalLoader from "./components/GlobalLoader";
import i18n from "./i18n";
import { useTranslation } from "react-i18next";
import AlertBar from "./components/AlertBar";

// import { checkProfile } from "./store/slices/companyInfoSlice";

import Footer from "./modules/shared/components/Footer/Footer";
import { SaudiRiyal } from "lucide-react";
import { fetchNotifications } from "@/modules/notifications/store/notificationsSlice";
import ScrollToTop from "./helper/ScrollToTop";
import {
  checkSubscription,
  ensureSubscriptionStatus,
  fetchSubscriptions,
} from "@/modules/subscriptions/store/subscriptionSlice";
import { fetchAnnouncements } from "@/modules/shared/store/announcementSlice";
import api from "@/modules/shared/api/axios";
import Swal from "sweetalert2";
import ModuleAccessGuard from "./components/ModuleAccessGuard";
import {
  SERVICE_MODULE_KEYS,
  getServiceModuleName,
  FREE_MODULE_KEYS,
} from "./config/serviceModules";
import { readPaymentContext } from "./modules/subscriptions/utils/paymentContext";

// Lazy load pages
const Login = lazy(() => import("./modules/auth/pages/Login"));
const Register = lazy(() => import("./modules/auth/pages/Register"));
const Forgot = lazy(() => import("./modules/auth/pages/Forgot"));
const Reset = lazy(() => import("./modules/auth/pages/Reset"));
const Dashboard = lazy(() =>
  import("./modules/tenders/pages/TendersDashboard/Dashboard")
);
const OffersDashboard = lazy(() =>
  import("./modules/offers/pages/OffersDashboard/OffersDashboard")
);
const ServicesDashboard = lazy(() =>
  import("./modules/services/pages/ServicesDashboard")
);
const MyInfoForm = lazy(() => import("./modules/companies/pages/MyInfoForm"));
const SubscriptionPage = lazy(() =>
  import("./modules/subscriptions/pages/SubscriptionPage")
);
const UpgradePlansPage = lazy(() =>
  import("./modules/subscriptions/pages/UpgradePlansPage")
);
import BankTransferPage from "./modules/subscriptions/pages/BankTransferPage";
const LandingPage = lazy(() => import("./modules/services/pages/LandingPage"));

const ALLOW_SUBSCRIBE_AFTER_REJECT_KEY = "allowSubscribeAfterRejection";

const TenderForm = lazy(() =>
  import("./modules/tenders/pages/TenderForm/TenderForm")
);
const TenderDashboard = lazy(() =>
  import("./modules/tenders/pages/TenderDashboard")
);
const TenderInfo = lazy(() =>
  import("./modules/tenders/pages/TenderInfo/TenderInfo")
);
const ReceivedProposal = lazy(() =>
  import("./modules/approvals/pages/ReceivedProposals")
);
const IncomingOffersPriceAnalysisDashboard = lazy(() =>
  import(
    "./modules/incoming-offers-price-analysis/pages/IncomingOffersPriceAnalysisDashboard"
  )
);
const PreferredSuppliers = lazy(() =>
  import("./modules/tenders/pages/PreferredSuppliers")
);
const NotificationPage = lazy(() =>
  import("./modules/notifications/pages/NotificationPage")
);
const OffersAnalysisPage = lazy(() =>
  import("./modules/tenders/pages/OffersAnalysisPage")
);
const ProfilePage = lazy(() =>
  import("./modules/profile/pages/ProfilePage")
);
const CreateDelegate = lazy(() =>
  import("./modules/delegation/pages/CreateDelegate")
);
const PermissionManagement = lazy(() =>
  import("./modules/delegation/pages/PermissionManagement")
);
const IncomingInvitations = lazy(() =>
  import("./modules/proposal-approvals/pages/IncomingInvitations")
);
const IncomingParticipationRequests = lazy(() =>
  import(
    "./modules/approvals/pages/IncomingParticipationRequests"
  )
);
const IncomingNegotiations = lazy(() =>
  import("./modules/negotiation-award/pages/IncomingNegotiations")
);
const PublisherNegotiationResponses = lazy(() =>
  import("./modules/negotiation-award/pages/PublisherNegotiationResponses")
);
const NegotiationDashboard = lazy(() =>
  import("./modules/negotiation-award/pages/NegotiationDashboard")
);
const PublisherNegotiationResponseDetails = lazy(() =>
  import("./modules/negotiation-award/pages/PublisherNegotiationResponseDetails")
);
const NegotiationStage = lazy(() =>
  import("./modules/negotiation-award/pages/NegotiationStage")
);
const NegotiationOfferDetails = lazy(() =>
  import("./modules/negotiation-award/pages/NegotiationOfferDetails")
);
const TenderAwarding = lazy(() =>
  import("./modules/negotiation-award/pages/TenderAwarding")
);
const TenderAwardingList = lazy(() =>
  import("./modules/negotiation-award/pages/TenderAwardingList")
);
const FinancialClaims = lazy(() =>
  import("./modules/financial-claims/pages/FinancialClaims")
);
const FinancialClaimsDashboard = lazy(() =>
  import("./modules/financial-claims/pages/FinancialClaimsDashboard")
);
const FinancialClaimForm = lazy(() =>
  import("./modules/financial-claims/pages/FinancialClaimForm")
);
const IncomingFinancialClaims = lazy(() =>
  import("./modules/financial-claims/pages/IncomingFinancialClaims")
);
const AwardedTendersToMe = lazy(() =>
  import("./modules/financial-claims/pages/AwardedTendersToMe")
);
const BankAccountManagement = lazy(() =>
  import("./modules/financial-claims/pages/BankAccountManagement")
);
const IncomingFinancialClaimDetails = lazy(() =>
  import("./modules/financial-claims/pages/IncomingFinancialClaimDetails")
);
const TenderApprovals = lazy(() =>
  import("./modules/approvals/pages/TenderApprovals")
);

const PaymentWidget = lazy(() =>
  import("./modules/subscriptions/pages/pay/PaymentWidget")
);
const ApplePayPage = lazy(() =>
  import("./modules/subscriptions/pages/pay/ApplePayPage")
);
const SadadPayPage = lazy(() =>
  import("./modules/subscriptions/pages/sadad/SadadPayPage")
);
const SadadPaymentResult = lazy(() =>
  import("./modules/subscriptions/pages/sadad/SadadPaymentResult")
);
const PaymentResult = lazy(() =>
  import("./modules/subscriptions/pages/pay/PaymentResult")
);
const ApplePayResult = lazy(() =>
  import("./modules/subscriptions/pages/pay/ApplePayResult")
);
const VatPage = lazy(() =>
  import("./modules/subscriptions/pages/vat/VatPage")
);
const DeclarationGuardPage = lazy(() =>
  import("./modules/subscriptions/pages/declarations/DeclarationGuardPage")
);
const DeclarationPendingPage = lazy(() =>
  import("./modules/subscriptions/pages/declarations/DeclarationPendingPage")
);
const FieldOfWorkClassificationPage = lazy(() =>
  import("./modules/work-classification/pages/FieldOfWorkClassificationPage")
);
const CompanyListPage = lazy(() =>
  import("./modules/companies/pages/CompanyListPage")
);
const PremiumSubscribersList = lazy(() =>
  import("./modules/companies/pages/PremiumSubscribersList")
);
const AuthorizationPendingPage = lazy(() =>
  import("./modules/auth/pages/AuthorizationPendingPage")
);
const ModulePlaceholderPage = lazy(() =>
  import("./modules/shared/pages/ModulePlaceholderPage")
);
const ExternalRedirectPage = lazy(() =>
  import("./modules/shared/pages/ExternalRedirectPage")
);
const SubscriptionManagementPage = lazy(() =>
  import("./modules/subscriptions/pages/SubscriptionManagementPage")
);
const ContractsDashboard = lazy(() =>
  import("./modules/contracts/pages/ContractsDashboard")
);
const ContractForm = lazy(() =>
  import("./modules/contracts/pages/ContractForm")
);
const IssuedContracts = lazy(() =>
  import("./modules/contracts/pages/IssuedContracts")
);
const IncomingContracts = lazy(() =>
  import("./modules/contracts/pages/IncomingContracts")
);
const ApprovedContracts = lazy(() =>
  import("./modules/contracts/pages/ApprovedContracts")
);
const ContractsApprovals = lazy(() =>
  import("./modules/contracts/pages/ContractsApprovals")
);

const ApprovalDashboard = lazy(() => {
  return import("./modules/approvals/pages/ApprovalDashboard")
})

const ProposalDashboard = lazy(() => {
  return import("./modules/proposal-approvals/pages/ProposalDashboard")
})

const DelegationDashboard = lazy(() => {
  return import("./modules/delegation/pages/DelegationDashboard")
})

function App() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, loaded: authLoaded } = useSelector((s) => s.auth);
  const isLoading = useSelector((s) => s?.ui?.loading);
  const company = useSelector((s) => s?.company);
  const subscriptions = useSelector((s) => s.subscriptions);
  const subscribed = useSelector((s) => s.subscriptions?.subscribed);
  const { t } = useTranslation();
  const storeAlerts = useSelector((s) => s.ui?.globalAlerts || []);
  const authorizedLimitMessage = useSelector(
    (s) => s.authorizedUsers?.limitMessage
  );
  const [dismissedAlertIds, setDismissedAlertIds] = useState([]);
  const subscriptionCheckAlertShownRef = useRef(false);
  const dismissedSet = useMemo(
    () => new Set(dismissedAlertIds),
    [dismissedAlertIds]
  );

  const location = useLocation();
  const path = location.pathname || "";
  const hideFooter = path === "/launch";
  const isProfileRoute = path.includes("/profile");
  const isSubscribeRoute = path === "/subscribe";
  const storedPaymentContext = useMemo(() => readPaymentContext(), [path]);
  const storedUpgradePayment = storedPaymentContext?.upgrade === true;

  // SADAD lock: once a SADAD invoice is generated, keep the user inside /pay/sadad
  // (or /subscribe) until payment is completed and the lock is cleared.
  useEffect(() => {
    let uuid = null;
    try {
      uuid = sessionStorage.getItem("tarh_sadad_tx_uuid");
    } catch (_) { }

    if (!uuid) return;

    // If the user is logged out, do not keep forcing the SADAD route.
    // Clear the lock because it is session-specific.
    if (!user) {
      try {
        sessionStorage.removeItem("tarh_sadad_tx_uuid");
      } catch (_) { }
      return;
    }

    if (subscribed && !storedUpgradePayment) {
      try {
        sessionStorage.removeItem("tarh_sadad_tx_uuid");
      } catch (_) { }
      return;
    }

    const allowed =
      path === "/pay/sadad" ||
      path === "/payment/sadad/result" ||
      path === "/subscribe" ||
      path === "/logout";
    if (!allowed) {
      navigate("/pay/sadad", { replace: true });
    }
  }, [path, navigate, subscribed, user, storedUpgradePayment]);
  // useEffect(() => {
  //   let warningShown = false;
  //   const warn = () => {
  //     if (warningShown) return;
  //     warningShown = true;
  //     try {
  //       console.clear();
  //     } catch (_) {
  //       // ignore
  //     }
  //     console.warn(
  //       "%cتحذير قانوني رسمي\n\nيُمنع منعاً باتاً العبث أو محاولة الدخول إلى الشفرة المصدرية أو بيانات النظام.\nجميع الحقوق محفوظة وفق الأنظمة واللوائح المعمول بها في المملكة العربية السعودية، وأي محاولة للتلاعب ستُعرّضك للمساءلة القانونية الكاملة وقد تُتخذ ضدك إجراءات قضائية صارمة.\nتابع استخدامك بشكل نظامي أو أغلق أدوات التطوير فوراً.",
  //       "color:red;font-size:20px;border-radius:8px;text-align:right;font-family: 'Tajawal', sans-serif !important"
  //     );
  //   };
  //   const detectDevTools = () => {
  //     const threshold = 150;
  //     const widthDiff = Math.abs(window.outerWidth - window.innerWidth);
  //     const heightDiff = Math.abs(window.outerHeight - window.innerHeight);
  //     if (widthDiff > threshold || heightDiff > threshold) {
  //       warn();
  //     }
  //   };
  //   const keyListener = (event) => {
  //     const key = event.key?.toLowerCase?.() || "";
  //     if (
  //       key === "f12" ||
  //       (event.shiftKey && key === "f12") ||
  //       (event.ctrlKey && event.shiftKey && key === "i")
  //     ) {
  //       warn();
  //     }
  //   };
  //   const resizeListener = () => detectDevTools();
  //   const detector = setInterval(detectDevTools, 1000);
  //   window.addEventListener("keydown", keyListener);
  //   window.addEventListener("resize", resizeListener);
  //   detectDevTools();
  //   return () => {
  //     clearInterval(detector);
  //     window.removeEventListener("keydown", keyListener);
  //     window.removeEventListener("resize", resizeListener);
  //   };
  // }, []);

  const lang = localStorage.getItem("lang") || "ar";
  const otpPending = (() => {
    try { return localStorage.getItem('otp_pending') === '1'; } catch (_) { return false; }
  })();
  const subscriptionStatusLoaded = useSelector(
    (s) => s.subscriptions?.statusLoaded
  );
  const hasPendingBankTransfer =
    !subscribed &&
    subscriptions?.currentPaymentMethod === "Bank Transfer" &&
    subscriptions?.currentPaymentVerificationStatus === "Pending" &&
    !!subscriptions?.currentPaymentReference;
  const hasRejectedBankTransfer =
    !subscribed &&
    subscriptions?.currentPaymentMethod === "Bank Transfer" &&
    subscriptions?.currentPaymentVerificationStatus === "Rejected";
  const hasApprovedBankTransfer =
    subscribed &&
    subscriptions?.currentPaymentMethod === "Bank Transfer" &&
    subscriptions?.currentPaymentVerificationStatus === "Approved";
  const bankTransferStepRedirect = hasPendingBankTransfer
    ? "/bank-transfer/pending"
    : hasRejectedBankTransfer
      ? "/bank-transfer/rejected"
      : null;
  const allowSubscribeAfterRejection = useMemo(() => {
    if (!hasRejectedBankTransfer) {
      return false;
    }
    if (typeof window === "undefined") {
      return false;
    }
    try {
      return (
        sessionStorage.getItem(ALLOW_SUBSCRIBE_AFTER_REJECT_KEY) === "1"
      );
    } catch {
      return false;
    }
  }, [hasRejectedBankTransfer, path]);

  useEffect(() => {
    if (!hasRejectedBankTransfer) {
      if (typeof window === "undefined") {
        return;
      }
      try {
        sessionStorage.removeItem(ALLOW_SUBSCRIBE_AFTER_REJECT_KEY);
      } catch {
        // ignore storage failures
      }
    }
  }, [hasRejectedBankTransfer]);
  const isAuthorizedOnly =
    !!user &&
    (user.role === "authorized_user" ||
      user.auth_context === "authorized_user");

  const canPublish = !!(
    user?.can_publish_tender || user?.can_publish_tenders
  );
  const canSubmit = !!(
    user?.can_submit_offers || user?.can_create_offers_draft
  );
  const canReceive = !!(
    user?.can_view_all_incoming_offers || user?.can_receive_offers
  );
  const hasAnyPermission = isAuthorizedOnly
    ? Object.keys(user || {}).some(
      (key) => key.startsWith("can_") && !!user?.[key]
    )
    : true;
  const ALL_TENDERS_ENABLED = true;
  const COMPANY_LIST_ENABLED = false;
  const allowedModules = Array.isArray(subscriptions?.allowedModules)
    ? subscriptions.allowedModules
    : [];

  const renderSubscriptionGateLoader = () => (
    <div className="p-6">
      <SkeletonLoader rows={5} />
    </div>
  );

  const withPermission = (
    element,
    {
      publish = false,
      submit = false,
      receive = false,
      any = false,
      permissions = [],
    } = {}
  ) => {
    // Company owners and non-authorized users are not restricted by these flags
    if (!isAuthorizedOnly) {
      return element;
    }

    const publishOk = !publish || canPublish;
    const submitOk = !submit || canSubmit;
    const receiveOk = !receive || canReceive;
    const anyOk = !any || hasAnyPermission;

    const permissionList = Array.isArray(permissions) ? permissions : [];
    const permissionOk =
      permissionList.length === 0 ||
      permissionList.some((key) => !!user?.[`can_${key}`]);

    const allowed = publishOk && submitOk && receiveOk && anyOk && permissionOk;

    if (!allowed) {
      return <Navigate to="/dashboard" replace />;
    }

    return element;
  };

  const hasModuleAccess = (moduleKey) => {
    if (!moduleKey) return true;
    if (!subscribed) return false;
    const candidates = String(moduleKey)
      .split(/[|,]/)
      .map((entry) => entry.trim())
      .filter(Boolean);
    if (candidates.length === 0) return true;

    return candidates.some((candidate) => {
      if (!SERVICE_MODULE_KEYS.includes(candidate)) return true;
      if (allowedModules.includes(candidate)) return true;
      return FREE_MODULE_KEYS.includes(candidate);
    });
  };

  const withModuleAccess = (element, moduleKey) => (
    <ModuleAccessGuard allowed={hasModuleAccess(moduleKey)}>
      {element}
    </ModuleAccessGuard>
  );

  const normalizeStatus = (value) =>
    typeof value === "string" ? value.toLowerCase() : value;

  const flowState = subscriptions?.flowState || {};

  const requiresVat =
    subscriptionStatusLoaded && subscribed
      ? !!flowState?.requires_vat && !flowState?.requires_payment_verification
      : false;

  const requiresFieldOfWorkClassification =
    subscriptionStatusLoaded && subscribed && !requiresVat
      ? !!user?.field_of_work_classifications_required
      : false;

  const declarationStatus = normalizeStatus(subscriptions?.declarationStatus);
  const documentVerified = user?.document_verified === true;
  const hasDeclarationFile =
    !!subscriptions?.declarationFile ||
    !!user?.Registration_Certificate ||
    !!subscriptions?.declarationFileUrl;
  const declarationWaitingApproval =
    hasDeclarationFile &&
    !documentVerified &&
    ["submitted", "pending", "in_review", "under_review"].includes(
      declarationStatus || ""
    );

  const requiresDeclaration =
    subscriptionStatusLoaded && subscribed && !requiresVat
      ? !!flowState?.requires_declaration
      : false;

  const documentPending = !documentVerified;

  const shouldShowDeclarationPending =
    subscribed &&
    ((requiresDeclaration && declarationWaitingApproval) ||
      (!requiresDeclaration && documentPending && hasDeclarationFile));

  const shouldShowDeclarationUpload =
    subscribed && requiresDeclaration && !hasDeclarationFile;

  const renderSubscriptionContent = (element) => {
    if (!user) {
      return <Navigate to="/login" replace />;
    }
    if (otpPending) {
      return <Navigate to="/login" replace />;
    }

    if (!subscriptionStatusLoaded) {
      return (
        <PrivateRoute>
          {renderSubscriptionGateLoader()}
        </PrivateRoute>
      );
    }

    // 2) Subscription guard
    if (!subscribed) {
      return (
        <Navigate
          to={bankTransferStepRedirect || "/dashboard"}
          replace
        />
      );
    }

    if (!flowState?.requires_payment_verification) {
      if (requiresVat && path !== "/vat") {
        return <Navigate to="/vat" replace />;
      }

      if (
        requiresFieldOfWorkClassification &&
        path !== "/field-of-work-classification"
      ) {
        return (
          <Navigate
            to="/field-of-work-classification?next=declaration"
            replace
          />
        );
      }

      if (shouldShowDeclarationPending && path !== "/declaration/pending") {
        return <Navigate to="/declaration/pending" replace />;
      }

      if (shouldShowDeclarationUpload && path !== "/declaration") {
        return <Navigate to="/declaration" replace />;
      }
    }

    return <PrivateRoute>{element}</PrivateRoute>;
  };

  const renderServicesLanding = () => {
    if (!user) {
      return <Navigate to="/login" replace />;
    }
    if (otpPending) {
      return <Navigate to="/login" replace />;
    }

    if (!subscriptionStatusLoaded) {
      return (
        <PrivateRoute>
          {renderSubscriptionGateLoader()}
        </PrivateRoute>
      );
    }

    if (bankTransferStepRedirect) {
      return <Navigate to={bankTransferStepRedirect} replace />;
    }

    const isExpired = String(subscriptions?.currentStatus || "").toLowerCase() === "expired";

    if (!subscribed && !isExpired) {
      return <Navigate to="/subscribe" replace />;
    }

    if (subscribed && !flowState?.requires_payment_verification) {
      if (requiresVat) {
        return <Navigate to="/vat" replace />;
      }

      if (requiresFieldOfWorkClassification) {
        return (
          <Navigate
            to="/field-of-work-classification?next=declaration"
            replace
          />
        );
      }

      if (shouldShowDeclarationPending) {
        return <Navigate to="/declaration/pending" replace />;
      }

      if (shouldShowDeclarationUpload) {
        return <Navigate to="/declaration" replace />;
      }
    }

    return (
      <PrivateRoute>
        <ServicesDashboard />
      </PrivateRoute>
    );
  };

  const renderRootRedirect = () => {
    if (!user) {
      return <Navigate to="/login" replace />;
    }
    if (otpPending) {
      return <Navigate to="/login" replace />;
    }

    if (!subscriptionStatusLoaded) {
      return renderSubscriptionGateLoader();
    }
    if (bankTransferStepRedirect) {
      return <Navigate to={bankTransferStepRedirect} replace />;
    }
    if (!flowState?.requires_payment_verification) {
      if (requiresVat) {
        return <Navigate to="/vat" replace />;
      }
      if (requiresFieldOfWorkClassification) {
        return <Navigate to="/field-of-work-classification?next=declaration" replace />;
      }
      if (shouldShowDeclarationPending) {
        return <Navigate to="/declaration/pending" replace />;
      }
      if (shouldShowDeclarationUpload) {
        return <Navigate to="/declaration" replace />;
      }
    }
    const isExpired = String(subscriptions?.currentStatus || "").toLowerCase() === "expired";
    if (!subscribed && !isExpired) {
      return <Navigate to="/subscribe" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  };

  const renderSetupRoute = (element) => {
    if (!user) {
      return <Navigate to="/login" replace />;
    }
    if (otpPending) {
      return <Navigate to="/login" replace />;
    }
    if (!subscriptionStatusLoaded) {
      return renderSubscriptionGateLoader();
    }
    if (!subscribed) {
      return <Navigate to="/subscribe" replace />;
    }
    return <PrivateRoute>{element}</PrivateRoute>;
  };

  const renderBankTransferRoute = () => {
    if (!user) {
      return <Navigate to="/login" replace />;
    }
    if (otpPending) {
      return <Navigate to="/login" replace />;
    }
    if (!subscriptionStatusLoaded) {
      return (
        <PrivateRoute>
          {renderSubscriptionGateLoader()}
        </PrivateRoute>
      );
    }
    return (
      <PrivateRoute>
        <BankTransferPage />
      </PrivateRoute>
    );
  };

  const renderLoginRoute = () => {
    if (user && !otpPending) {
      if (!subscriptionStatusLoaded) {
        return renderSubscriptionGateLoader();
      }
      const isExpired = String(subscriptions?.currentStatus || "").toLowerCase() === "expired";
      return <Navigate to={subscribed || isExpired ? "/dashboard" : "/subscribe"} replace />;
    }
    return <Login />;
  };

  useEffect(() => {
    let cancelled = false;
    const loadUser = async () => {
      dispatch(setLoading(true));
      dispatch(setProgress(0));
      try {
        await dispatch(fetchUser()).unwrap();
      } catch (_) {
        // ignore
      } finally {
        if (cancelled) return;
        dispatch(setProgress(100));
        setTimeout(() => {
          dispatch(setLoading(false));
          dispatch(setProgress(0));
        }, 600);
        const lang = localStorage.getItem("lang") || "ar";
        i18n.changeLanguage(lang);
        document.documentElement.setAttribute(
          "dir",
          lang === "ar" ? "rtl" : "ltr"
        );
        document.documentElement.lang = lang;
      }
    };
    loadUser();
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  useEffect(() => {
    if (!authLoaded || !user) {
      return;
    }
    let cancelled = false;

    const preload = async () => {
      const tasks = [
        dispatch(fetchSubscriptions()).unwrap().catch(() => { }),
        // dispatch(fetchAnnouncements()).unwrap().catch(() => {}),
        dispatch(ensureSubscriptionStatus()).unwrap(),
      ];
      try {
        await Promise.all(tasks);
        dispatch(removeGlobalAlert("subscription-status-offline"));
      } catch (_) {
        if (cancelled) return;
        if (subscriptionCheckAlertShownRef.current) return;
        subscriptionCheckAlertShownRef.current = true;

        Swal.fire({
          title: "Connection issue",
          text: "We couldn't verify your subscription status. Please retry.",
          icon: "warning",
          iconColor: "#dc2626",
          showCancelButton: true,
          confirmButtonText: "Retry",
          cancelButtonText: "Logout",
        }).then(async (result) => {
          if (result.isConfirmed) {
            try {
              await dispatch(checkSubscription()).unwrap();
              dispatch(removeGlobalAlert("subscription-status-offline"));
            } catch {
              dispatch(
                addGlobalAlert({
                  id: "subscription-status-offline",
                  type: "warning",
                  title: "Still offline",
                  message:
                    "We still couldn't verify your subscription status. Please try again in a moment.",
                })
              );
            }
            return;
          }
          dispatch(logoutThunk());
        });
      }
    };

    preload();
    return () => {
      cancelled = true;
    };
  }, [authLoaded, user?.uuid, user?.id, user?.email, dispatch]);

  useEffect(() => {
    if (user?.uuid && subscribed && user?.document_verified !== false) {
      dispatch(fetchNotifications());
    }
  }, [dispatch, user?.uuid, subscribed, user?.document_verified]);

  // const {announcementLoading} = useSelector((s)=>s.announcements.announcementLoading)

  const showDocBanner = false;

  const derivedAlerts = useMemo(() => {
    const alerts = [];
    if (showDocBanner) {
      alerts.push({
        id: "document-verification",
        type: "warning",
        title: t("document_verification_banner_title"),
        message: t("document_verification_banner_desc"),
      });
    }
    if (authorizedLimitMessage) {
      alerts.push({
        id: "subscription-limit",
        type: "danger",
        title: t("subscription_limit_reached"),
        message: authorizedLimitMessage,
      });
    }
    return alerts;
  }, [showDocBanner, authorizedLimitMessage, t]);

  const mergedAlerts = useMemo(() => {
    const map = new Map();
    [...storeAlerts, ...derivedAlerts].forEach((alert) => {
      if (!alert || !alert.id) return;
      map.set(alert.id, alert);
    });
    return Array.from(map.values());
  }, [storeAlerts, derivedAlerts]);

  const visibleAlerts = mergedAlerts.filter(
    (alert) => !dismissedSet.has(alert.id)
  );

  const handleAlertDismiss = (id) => {
    setDismissedAlertIds((prev) =>
      prev.includes(id) ? prev : [...prev, id]
    );
  };

  useEffect(() => {
    if (!showDocBanner) {
      setDismissedAlertIds((prev) =>
        prev.includes("document-verification")
          ? prev.filter((id) => id !== "document-verification")
          : prev
      );
    }
  }, [showDocBanner]);

  useEffect(() => {
    if (!authorizedLimitMessage) {
      setDismissedAlertIds((prev) =>
        prev.includes("subscription-limit")
          ? prev.filter((id) => id !== "subscription-limit")
          : prev
      );
    }
  }, [authorizedLimitMessage]);

  const authorizationPending = useMemo(() => {
    if (!authLoaded || !user) return false;

    const hasSubscription = !!subscribed;
    const hasUploadedAuthorization =
      user.Registration_Certificate === true ||
      !!user.authorizationUrl;

    return (
      hasSubscription &&
      hasUploadedAuthorization &&
      user.document_verified === false
    );
  }, [authLoaded, user, subscribed]);

  if (!authLoaded) {
    return (
      <div className="flex items-center justify-center h-screen  dark:bg-slate-800 dark:text-white bg-gray-100">
        <GlobalLoader />
      </div>
    );
  }

  // Subscription gating happens via routes below

  const formatArEnNumber = (n, grouping = false, showCurrency = false) => {
    if (n === null || n === undefined || isNaN(n)) return "0";

    const locale = lang === "en" ? "en-US" : "ar-SA-u-nu-latn";

    const options = {
      useGrouping: grouping,
      style: "decimal",
    };

    const formatted = new Intl.NumberFormat(locale, options).format(n);

    if (showCurrency) {
      return (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "4px",
            color: "#1B8354", // number + icon color
            fontWeight: 700,
          }}
        >
          {lang === "ar" ? (
            <>
              <span>{formatted}</span>
              <SaudiRiyal size={18} color="#1B8354" />
            </>
          ) : (
            <>
              <span>{formatted}</span>
              <SaudiRiyal size={18} color="#1B8354" />
            </>
          )}
        </span>
      );
    }

    return (
      <span style={{ color: "#1B8354", fontWeight: 700 }}>{formatted}</span>
    );
  };

  const formatDateByLocale = (dateInput, formatOptions = null) => {
    if (!dateInput) return null;

    const locale = lang === "en" ? "en-US" : "en-SA";
    const defaultOptions = {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    };
    const options =
      formatOptions && Object.keys(formatOptions || {}).length > 0
        ? { ...formatOptions }
        : defaultOptions;

    const normalizeIsoCandidate = (value) => {
      if (typeof value !== "string") {
        return value;
      }
      const trimmed = value.trim();
      if (!trimmed) return trimmed;
      if (trimmed.includes("T")) {
        return trimmed;
      }
      if (trimmed.includes(" ")) {
        return trimmed.replace(" ", "T");
      }
      return trimmed;
    };

    const parseZohoDate = (value) => {
      const parts = value?.match(
        /(\d{2})-(\w{3})-(\d{4}) (\d{2}):(\d{2}):(\d{2}) (AM|PM)/
      );
      if (!parts) return null;

      const monthMap = {
        Jan: 0,
        Feb: 1,
        Mar: 2,
        Apr: 3,
        May: 4,
        Jun: 5,
        Jul: 6,
        Aug: 7,
        Sep: 8,
        Oct: 9,
        Nov: 10,
        Dec: 11,
      };

      let [, day, month, year, hours, minutes, seconds, ampm] = parts;
      hours = parseInt(hours, 10);
      if (ampm === "PM" && hours !== 12) {
        hours += 12;
      }
      if (ampm === "AM" && hours === 12) {
        hours = 0;
      }

      const dateObj = new Date(
        parseInt(year, 10),
        monthMap[month],
        parseInt(day, 10),
        hours,
        parseInt(minutes, 10),
        parseInt(seconds, 10)
      );

      return Number.isNaN(dateObj.getTime()) ? null : dateObj;
    };

    const resolveDate = (value) => {
      if (!value) return null;

      if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
      }

      if (typeof value === "number") {
        const fromNumber = new Date(value);
        return Number.isNaN(fromNumber.getTime()) ? null : fromNumber;
      }

      if (typeof value === "string") {
        const isoCandidate = normalizeIsoCandidate(value);
        const fromIso = new Date(isoCandidate);
        if (!Number.isNaN(fromIso.getTime())) {
          return fromIso;
        }
        const fallback = new Date(value);
        if (!Number.isNaN(fallback.getTime())) {
          return fallback;
        }
        return parseZohoDate(value);
      }

      return null;
    };

    const dateObj = resolveDate(dateInput);
    if (!dateObj) return null;

    return new Intl.DateTimeFormat(locale, options).format(dateObj);
  };

  const getModuleTitle = (moduleKey) =>
    getServiceModuleName(moduleKey, lang, "dashboard");

  const onSetupRoute = path === "/vat" || path === "/subscribe";
  const showAlertBar = !onSetupRoute && visibleAlerts.length > 0;

  return (
    <div className="app-shell flex flex-col bg-white text-gray-900 dark:bg-slate-700 dark:text-white transition-colors duration-300">
      <Navbar />
      {showAlertBar && (
        <div className="mt-16 lg:mt-24 -mb-15 lg:-mb-20">
          <AlertBar alerts={visibleAlerts} onDismiss={handleAlertDismiss} />
        </div>
      )}

      <main className="flex-grow container mx-auto pt-26">
        {authorizationPending && !isProfileRoute ? (
          <Suspense
            fallback={
              <div className="p-4">
                <SkeletonLoader rows={4} />
              </div>
            }
          >
            <PrivateRoute>
              <AuthorizationPendingPage
                onRefresh={() => {
                  dispatch(fetchUser());
                }}
              />
            </PrivateRoute>
          </Suspense>
        ) : (
          <Suspense
            fallback={
              <div className="p-4">
                <SkeletonLoader rows={4} />
              </div>
            }
          >
            <Routes>
              {/* Root */}
              <Route
                path="/"
                element={renderRootRedirect()}
              />

              {/* Public */}
              <Route
                path="/login"
                element={renderLoginRoute()}
              />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot" element={<Forgot />} />
              <Route path="/reset" element={<Reset />} />

              {/* Private */}
              {/* MyInfo */}
              {/* <Route
                path="/myinfo"
                element={
                  user ? (
                    otpPending ? (
                      <Navigate to="/login" replace />
                    ) : (
                      <PrivateRoute>
                        <MyInfoForm />
                      </PrivateRoute>
                    )
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              /> */}
              <Route
                path="/dashboard"
                element={renderServicesLanding()}
              />
              <Route
                path="/tenders-dashboard"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    <Dashboard formatArEnNumber={formatArEnNumber} />,
                    "tenders_offers_management"
                  )
                )}
              />
              <Route
                path="/offers-dashboard"
                element={renderSubscriptionContent(
                  withModuleAccess(
                      withPermission(
                        <OffersDashboard formatArEnNumber={formatArEnNumber} />,
                        {
                          permissions: [
                            "view_all_submissions",
                            "view_my_submissions",
                            "create_offers_draft",
                            "submit_offers",
                            "move_public_tender_to_my_submissions",
                            "request_to_join_tender",
                          ],
                        }
                      ),
                      "offers_management"
                    )
                  )}
              />
              <Route
                path="/negotiation-dashboard"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    <NegotiationDashboard formatArEnNumber={formatArEnNumber} />,
                    "negotiation_award_management"
                  )
                )}
              />
              <Route
                path="/financial-claims-dashboard"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    <FinancialClaimsDashboard
                      formatArEnNumber={formatArEnNumber}
                    />,
                    "financial_claims_management"
                  )
                )}
              />
              <Route
                path="/approvals-dashboard"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    <ApprovalDashboard
                      formatArEnNumber={formatArEnNumber}
                    />,
                    "approvals_requests_management"
                  )
                )}
              />
              <Route
                path="/tender-form"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<TenderForm />, {
                      permissions: ["create_tender", "publish_tender"],
                    }),
                    "tenders_offers_management"
                  )
                )}
              />

              <Route
                path="/draft/:recId/edit"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<TenderForm />, {
                      permissions: [
                        "create_tender",
                        "edit_tender_general_info",
                        "publish_tender",
                      ],
                    }),
                    "tenders_offers_management"
                  )
                )}
              />
              <Route
                path="/all-tenders"
                element={
                  ALL_TENDERS_ENABLED
                    ? renderSubscriptionContent(
                      withModuleAccess(
                        <TenderDashboard />,
                        "offers_management"
                      )
                    )
                    : <Navigate to="/dashboard" replace />
                }
              />

              <Route
                path="/my-tenders"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<TenderDashboard />, {
                      permissions: ["view_published_tenders", "view_my_submitted_competitions"],
                    }),
                    "tenders_offers_management"
                  )
                )}
              />

              <Route
                path="/my-offers"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<TenderDashboard />, {
                      permissions: ["view_all_submissions", "view_my_submissions"],
                    }),
                    "offers_management"
                  )
                )}
              />
              <Route
                path="/proposal-approvals"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<ProposalDashboard />, {
                      permissions: ["accept_reject_invitations"],
                    }),
                    "proposal_approvals_management"
                  )
                )}
              />
              <Route
                path="/incoming-invitations"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<IncomingInvitations />, {
                      permissions: ["accept_reject_invitations"],
                    }),
                    "proposal_approvals_management"
                  )
                )}
              />
              <Route
                path="/incoming-participation-requests"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<IncomingParticipationRequests />, {
                      permissions: ["publish_tender"],
                    }),
                    "approvals_requests_management"
                  )
                )}
              />
              <Route
                path="/incoming-negotiation-requests"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<IncomingNegotiations />, {
                      permissions: [
                        "view_incoming_negotiations_for_review",
                        "accept_negotiation",
                        "submit_offers",
                        "create_offers_draft",
                      ],
                    }),
                    "negotiation_award_management"
                  )
                )}
              />
              <Route
                path="/financial-claims"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<FinancialClaims />, {
                      permissions: ["view_financial_claims"],
                    }),
                    "financial_claims_management"
                  )
                )}
              />
              <Route
                path="/financial-claims/new"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<FinancialClaimForm />, {
                      permissions: ["create_financial_claim"],
                    }),
                    "financial_claims_management"
                  )
                )}
              />
              <Route
                path="/financial-claims/bank-accounts"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<BankAccountManagement />, {
                      permissions: ["bank_account_management"],
                    }),
                    "financial_claims_management"
                  )
                )}
              />
              <Route
                path="/financial-claims/awarded-tenders"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<AwardedTendersToMe />, {
                      permissions: ["create_financial_claim"],
                    }),
                    "financial_claims_management"
                  )
                )}
              />
              <Route
                path="/financial-claims/:id"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<FinancialClaimForm />, {
                      permissions: ["create_financial_claim"],
                    }),
                    "financial_claims_management"
                  )
                )}
              />
              <Route
                path="/publisher-negotiation-responses"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<PublisherNegotiationResponses />, {
                      permissions: ["view_pending_supplier_negotiations"],
                    }),
                    "negotiation_award_management"
                  )
                )}
              />
              <Route
                path="/publisher-negotiations/:id"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<PublisherNegotiationResponseDetails />, {
                      permissions: ["view_pending_supplier_negotiations"],
                    }),
                    "negotiation_award_management"
                  )
                )}
              />
              <Route
                path="/incoming-financial-claims"
                element={renderSubscriptionContent(
                  withModuleAccess(
                      withPermission(<IncomingFinancialClaims />, {
                        permissions: [
                          "review_financial_claim",
                          "review_certificate_completion",
                          "approve_claim_amount",
                          "approve_claim_payment_processing",
                          "approve_claim_payment",
                      ],
                    }),
                    "financial_claims_management"
                  )
                )}
              />
              <Route
                path="/incoming-financial-claims/:id"
                element={renderSubscriptionContent(
                  withModuleAccess(
                      withPermission(<IncomingFinancialClaimDetails />, {
                        permissions: [
                          "review_financial_claim",
                          "review_certificate_completion",
                          "approve_claim_amount",
                          "approve_claim_payment_processing",
                          "approve_claim_payment",
                      ],
                    }),
                    "financial_claims_management"
                  )
                )}
              />
              <Route
                path="/tender/:recId/view"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    <TenderInfo
                      formatArEnNumber={formatArEnNumber}
                      formatDateByLocale={formatDateByLocale}
                    />,
                    "tenders_offers_management"
                  )
                )}
              />

              <Route
                path="/mytender/:recId/view"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(
                        <TenderInfo
                          formatArEnNumber={formatArEnNumber}
                          formatDateByLocale={formatDateByLocale}
                        />,
                        {
                          permissions: [
                            "view_published_tenders",
                            "view_my_submitted_competitions",
                          ],
                        }
                      ),
                      "tenders_offers_management"
                    )
                )}
              />
              <Route
                path="/mytender/:recId/edit"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<TenderForm />, {
                      permissions: [
                        "edit_tender_general_info",
                        "publish_tender",
                        "create_tender",
                      ],
                    }),
                    "tenders_offers_management"
                  )
                )}
              />
              <Route
                path="/mytender/:recId/extend"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<TenderForm />, {
                      permissions: ["extend_tender_duration"],
                    }),
                    "tenders_offers_management"
                  )
                )}
              />
              <Route
                path="/mytender/:recId/edit-boq"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<TenderForm />, {
                      permissions: ["edit_boq"],
                    }),
                    "tenders_offers_management"
                  )
                )}
              />
              <Route
                path="/myoffer/:recId/view"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(
                      <TenderInfo
                        formatArEnNumber={formatArEnNumber}
                        formatDateByLocale={formatDateByLocale}
                      />,
                      { submit: true }
                    ),
                    "offers_management"
                  )
                )}
              />

              <Route
                path="/submitOffer/:recId/active"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(
                      <TenderInfo
                        formatArEnNumber={formatArEnNumber}
                        formatDateByLocale={formatDateByLocale}
                      />,
                      { submit: true }
                    ),
                    "offers_management"
                  )
                )}
              />
              <Route
                path="/mytender/:recId/negotiation"
                element={renderSubscriptionContent(
                    withModuleAccess(
                      withPermission(<NegotiationStage />, {
                      permissions: ["create_negotiation_offer_draft"],
                      }),
                      "negotiation_award_management"
                    )
                )}
              />
              <Route
                path="/mytender/:recId/award"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<TenderAwarding />, {
                      permissions: ["award_supplier"],
                    }),
                    "negotiation_award_management"
                  )
                )}
              />
              <Route
                path="/tenders-pending-awarding"
                element={renderSubscriptionContent(
                  withModuleAccess(
                      withPermission(<TenderAwardingList mode="pending" />, {
                        permissions: ["tenders_pending_awarding"],
                      }),
                    "negotiation_award_management"
                  )
                )}
              />
              <Route
                path="/awarded-tenders"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<TenderAwardingList mode="awarded" />, {
                      permissions: ["view_award_approvals"],
                    }),
                    "negotiation_award_management"
                  )
                )}
              />
              <Route
                path="/tenders-awarded-to-me"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<TenderAwardingList mode="awarded-to-me" />, {
                      permissions: [
                        "view_tenders_awarded_to_me",
                        "view_award_approvals",
                      ],
                    }),
                    "negotiation_award_management"
                  )
                )}
              />

              <Route
                path="/submitOffer/:recId/draft"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(
                      <TenderInfo
                        formatArEnNumber={formatArEnNumber}
                        formatDateByLocale={formatDateByLocale}
                      />,
                      { submit: true }
                    ),
                    "offers_management"
                  )
                )}
              />
              <Route
                path="/negotiations/:id"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<NegotiationOfferDetails />, {
                      permissions: ["submit_offers", "create_offers_draft"],
                    }),
                    "negotiation_award_management"
                  )
                )}
              />

              <Route
                path="/incoming-offers-price-analysis-dashboard"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<IncomingOffersPriceAnalysisDashboard />, {
                      permissions: ["view_all_incoming_offers"],
                    }),
                    "incoming_offers_price_analysis_management"
                  )
                )}
              />
              <Route
                path="/received-proposals"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<ReceivedProposal />, {
                      permissions: ["view_all_incoming_offers"],
                    }),
                    "incoming_offers_price_analysis_management"
                  )
                )}
              />
              <Route
                path="/tender-approvals"
                element={renderSubscriptionContent(
                    withModuleAccess(
                      withPermission(<TenderApprovals />, {
                      permissions: ["publish_tender"],
                      }),
                      "approvals_requests_management"
                    )
                )}
              />
              <Route
                path="/tender-approvals/:recId/view"
                element={renderSubscriptionContent(
                    withModuleAccess(
                      withPermission(<TenderForm />, {
                      permissions: ["publish_tender"],
                      }),
                      "approvals_requests_management"
                    )
                )}
              />
              <Route
                path="/contracts-approvals"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<ContractsApprovals />, {
                      permissions: [
                        "view_contract_approval_requests",
                        "approve_reject_contract",
                      ],
                    }),
                    "contracts_management"
                  )
                )}
              />

              <Route
                path="/report/tender/:recId/offers-analysis"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<OffersAnalysisPage />, {
                      permissions: ["view_all_incoming_offers"],
                    }),
                    "tenders_offers_management"
                  )
                )}
              />

              <Route
                path="/profile"
                element={
                  user ? (
                    <PrivateRoute>
                      <ProfilePage />
                    </PrivateRoute>
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/delegation-dashboard"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    <DelegationDashboard
                      title={getModuleTitle("delegation_permissions_management")}
                      subtitle={t("delegation_dashboard_placeholder")}
                    />,
                    "delegation_permissions_management"
                  )
                )}
              />
              <Route
                path="/delegates/new"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<CreateDelegate />, { any: true }),
                    "delegation_permissions_management"
                  )
                )}
              />
              <Route
                path="/delegates/permissions"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<PermissionManagement />, { any: true }),
                    "delegation_permissions_management"
                  )
                )}
              />
              <Route
                path="/pay"
                element={
                  user ? (
                    otpPending ? (
                      <Navigate to="/login" replace />
                    ) : !subscriptionStatusLoaded ? (
                      <PrivateRoute>
                        {renderSubscriptionGateLoader()}
                      </PrivateRoute>
                    ) : (
                      (() => {
                        const allowUpgradePayment =
                          location.state?.upgrade === true ||
                          location.state?.type === "modules" ||
                          Array.isArray(location.state?.module_ids) ||
                          storedPaymentContext?.upgrade === true ||
                          storedPaymentContext?.type === "modules";
                        if (subscribed && !allowUpgradePayment) {
                          return <Navigate to="/dashboard" replace />;
                        }

                        if (hasPendingBankTransfer) {
                          return (
                            <Navigate
                              to="/bank-transfer/pending"
                              replace
                            />
                          );
                        }

                        return (
                          <PrivateRoute>
                            <PaymentWidget />
                          </PrivateRoute>
                        );
                      })()
                    )
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/pay/apple"
                element={
                  user ? (
                    otpPending ? (
                      <Navigate to="/login" replace />
                    ) : !subscriptionStatusLoaded ? (
                      <PrivateRoute>
                        {renderSubscriptionGateLoader()}
                      </PrivateRoute>
                    ) : (
                      (() => {
                        const allowUpgradePayment =
                          location.state?.upgrade === true ||
                          storedPaymentContext?.upgrade === true;
                        if (subscribed && !allowUpgradePayment) {
                          return <Navigate to="/dashboard" replace />;
                        }

                        if (hasPendingBankTransfer) {
                          return (
                            <Navigate
                              to="/bank-transfer/pending"
                              replace
                            />
                          );
                        }

                        return (
                          <PrivateRoute>
                            <ApplePayPage />
                          </PrivateRoute>
                        );
                      })()
                    )
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/pay/sadad"
                element={
                  user ? (
                    otpPending ? (
                      <Navigate to="/login" replace />
                    ) : !subscriptionStatusLoaded ? (
                      <PrivateRoute>
                        {renderSubscriptionGateLoader()}
                      </PrivateRoute>
                    ) : (
                      (() => {
                        const allowUpgradePayment =
                          location.state?.upgrade === true ||
                          location.state?.type === "modules" ||
                          Array.isArray(location.state?.module_ids) ||
                          storedPaymentContext?.upgrade === true ||
                          storedPaymentContext?.type === "modules";
                        if (subscribed && !allowUpgradePayment) {
                          return <Navigate to="/dashboard" replace />;
                        }

                        if (hasPendingBankTransfer) {
                          return (
                            <Navigate to="/bank-transfer/pending" replace />
                          );
                        }

                        return (
                          <PrivateRoute>
                            <SadadPayPage />
                          </PrivateRoute>
                        );
                      })()
                    )
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/bank-transfer"
                element={<Navigate to="/bank-transfer/form" replace />}
              />
              <Route
                path="/bank-transfer/:step"
                element={renderBankTransferRoute()}
              />
              <Route
                path="/vat"
                element={renderSetupRoute(
                  requiresVat ? (
                    <VatPage />
                  ) : requiresFieldOfWorkClassification ? (
                    <Navigate to="/field-of-work-classification?next=declaration" replace />
                  ) : shouldShowDeclarationUpload ? (
                    <Navigate to="/declaration" replace />
                  ) : shouldShowDeclarationPending ? (
                    <Navigate to="/declaration/pending" replace />
                  ) : (
                    <Navigate to={subscribed ? "/dashboard" : "/subscribe"} replace />
                  )
                )}
              />
              <Route
                path="/declaration"
                element={renderSetupRoute(
                  requiresVat ? (
                    <Navigate to="/vat" replace />
                  ) : requiresFieldOfWorkClassification ? (
                    <Navigate to="/field-of-work-classification?next=declaration" replace />
                  ) : shouldShowDeclarationUpload ? (
                    <DeclarationGuardPage />
                  ) : shouldShowDeclarationPending ? (
                    <Navigate to="/declaration/pending" replace />
                  ) : (
                    <Navigate to="/dashboard" replace />
                  )
                )}
              />
              <Route
                path="/declaration/pending"
                element={renderSetupRoute(
                  requiresVat ? (
                    <Navigate to="/vat" replace />
                  ) : requiresFieldOfWorkClassification ? (
                    <Navigate to="/field-of-work-classification?next=declaration" replace />
                  ) : shouldShowDeclarationPending ? (
                    <DeclarationPendingPage />
                  ) : requiresDeclaration ? (
                    <Navigate to="/declaration" replace />
                  ) : (
                    <Navigate to="/dashboard" replace />
                  )
                )}
              />
              <Route
                path="/field-of-work-classification"
                element={renderSetupRoute(
                  requiresVat ? (
                    <Navigate to="/vat" replace />
                  ) : shouldShowDeclarationPending ? (
                    <Navigate to="/declaration/pending" replace />
                  ) : !requiresFieldOfWorkClassification &&
                    shouldShowDeclarationUpload ? (
                    <Navigate to="/declaration" replace />
                  ) : (
                    <FieldOfWorkClassificationPage />
                  )
                )}
              />
              <Route
                path="/work-classification-management"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    <FieldOfWorkClassificationPage />,
                    "work_classification_management"
                  )
                )}
              />
              <Route
                path="/payment/result"
                element={
                  user ? (
                    otpPending ? (
                      <Navigate to="/login" replace />
                    ) : !subscriptionStatusLoaded ? (
                      <PrivateRoute>
                        {renderSubscriptionGateLoader()}
                      </PrivateRoute>
                    ) : (
                      <PrivateRoute>
                        <PaymentResult />
                      </PrivateRoute>
                    )
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/payment/apple/result"
                element={
                  user ? (
                    otpPending ? (
                      <Navigate to="/login" replace />
                    ) : !subscriptionStatusLoaded ? (
                      <PrivateRoute>
                        {renderSubscriptionGateLoader()}
                      </PrivateRoute>
                    ) : (
                      <PrivateRoute>
                        <ApplePayResult />
                      </PrivateRoute>
                    )
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/payment/sadad/result"
                element={
                  user ? (
                    otpPending ? (
                      <Navigate to="/login" replace />
                    ) : !subscriptionStatusLoaded ? (
                      <PrivateRoute>
                        {renderSubscriptionGateLoader()}
                      </PrivateRoute>
                    ) : (
                      <PrivateRoute>
                        <SadadPaymentResult />
                      </PrivateRoute>
                    )
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />

              {/* <Route
                path="/paytest"
                element={                 
                      <PaymentWidget />
                }
              /> */}



              <Route
                path="/preffered-supplier"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(
                      <PreferredSuppliers
                        formatArEnNumber={formatArEnNumber}
                        formatDateByLocale={formatDateByLocale}
                      />,
                      { any: true }
                    ),
                    "preferred_vendors_management"
                  )
                )}
              />

              <Route
                path="/company-list"
                element={
                  COMPANY_LIST_ENABLED
                    ? renderSubscriptionContent(
                      withModuleAccess(
                        withPermission(<CompanyListPage />, {
                          permissions: ["publish_tender"],
                        }),
                        "companies_management"
                      )
                    )
                    : <Navigate to="/dashboard" replace />
                }
              />

              <Route
                path="/premium-subscribers"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<PremiumSubscribersList />, { any: true }),
                    "companies_management"
                  )
                )}
              />

              <Route
                path="/notifications"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    <NotificationPage />,
                    "notifications_management"
                  )
                )}
              />

              <Route
                path="/subscription-management"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    <SubscriptionManagementPage />,
                    "subscription_management"
                  )
                )}
              />
              <Route
                path="/upgrade"
                element={renderSubscriptionContent(
                  <UpgradePlansPage />
                )}
              />

              <Route
                path="/communication-management"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    <ExternalRedirectPage
                      url="https://tarhplatform.com/contact"
                      titleKey="communication_redirect_title"
                      descriptionKey="communication_redirect_message"
                      actionLabelKey="communication_redirect_action"
                    />,
                    "communication_management"
                  )
                )}
              />

              <Route
                path="/contracts-management"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    <Navigate to="/contracts-dashboard" replace />,
                    "contracts_management"
                  )
                )}
              />
              <Route
                path="/contracts-dashboard"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    <ContractsDashboard formatArEnNumber={formatArEnNumber} />,
                    "contracts_management"
                  )
                )}
              />
              <Route
                path="/contracts-management/create"
                element={renderSubscriptionContent(
                    withModuleAccess(
                      withPermission(<ContractForm />, {
                      permissions: ["create_contract_draft"],
                      }),
                      "contracts_management"
                    )
                )}
              />
              <Route
                path="/contracts-management/issued"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<IssuedContracts />, {
                      permissions: ["view_issued_contracts"],
                    }),
                    "contracts_management"
                  )
                )}
              />
              <Route
                path="/contracts-management/:contractId"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<ContractForm />, {
                      permissions: [
                        "view_issued_contracts",
                        "review_incoming_contract",
                        "view_approved_contracts",
                        "view_contract_approval_requests",
                      ],
                    }),
                    "contracts_management"
                  )
                )}
              />
              <Route
                path="/financial-claims/contracts/:contractId"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(
                      <ContractForm
                        claimsViewMode={true}
                        supplierListPath="/financial-claims/contracts/incoming"
                        issuerListPath="/financial-claims/contracts/approved"
                      />,
                      {
                        permissions: [
                          "view_issued_contracts",
                          "review_incoming_contract",
                          "view_approved_contracts",
                          "view_contract_approval_requests",
                        ],
                      }
                    ),
                    "financial_claims_management"
                  )
                )}
              />
              <Route
                path="/contracts-management/incoming"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<IncomingContracts />, {
                      permissions: ["review_incoming_contract"],
                    }),
                    "contracts_management"
                  )
                )}
              />
              <Route
                path="/financial-claims/contracts/incoming"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(
                      <IncomingContracts
                        detailBasePath="/financial-claims/contracts"
                      />,
                      {
                        permissions: ["review_incoming_contract"],
                      }
                    ),
                    "financial_claims_management"
                  )
                )}
              />
              <Route
                path="/contracts-management/approved"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(<ApprovedContracts />, {
                      permissions: ["view_approved_contracts"],
                    }),
                    "contracts_management"
                  )
                )}
              />
              <Route
                path="/financial-claims/contracts/approved"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    withPermission(
                      <ApprovedContracts
                        detailBasePath="/financial-claims/contracts"
                      />,
                      {
                        permissions: ["view_approved_contracts"],
                      }
                    ),
                    "financial_claims_management"
                  )
                )}
              />

              <Route
                path="/platform-guide"
                element={renderSubscriptionContent(
                  withModuleAccess(
                    <ExternalRedirectPage
                      url="http://tarhplatform.com/assets/%D9%84%D8%AF%D9%84%D9%8A%D9%84%20%D8%A7%D9%84%D8%AA%D8%B9%D8%B1%D9%8A%D9%81%D9%8A%20%D9%84%D9%85%D9%86%D8%B5%D8%A9%20%D8%B7%D8%B1%D8%AD.pdf"
                      titleKey="platform_guide_redirect_title"
                      descriptionKey="platform_guide_redirect_message"
                      actionLabelKey="platform_guide_redirect_action"
                    />,
                    "platform_guide"
                  )
                )}
              />

              <Route
                path="/subscribe"
                element={
                  user ? (
                    otpPending ? (
                      <Navigate to="/login" replace />
                    ) : (!subscriptionStatusLoaded ? (
                      <PrivateRoute>
                        {renderSubscriptionGateLoader()}
                      </PrivateRoute>
                    ) : (
                      (() => {
                        // If already subscribed via other methods, go straight to dashboard.
                        // For Bank Transfer with "Approved" status, keep the user on the
                        // subscription page so the approval modal can be shown.
                        const allowSubscribeFromRejected =
                          allowSubscribeAfterRejection && hasRejectedBankTransfer;

                        if (bankTransferStepRedirect && !allowSubscribeFromRejected) {
                          return <Navigate to={bankTransferStepRedirect} replace />;
                        }

                        if (!flowState?.requires_payment_verification) {
                          if (requiresVat) {
                            return <Navigate to="/vat" replace />;
                          }

                          if (shouldShowDeclarationPending) {
                            return <Navigate to="/declaration/pending" replace />;
                          }

                          if (shouldShowDeclarationUpload) {
                            return <Navigate to="/declaration" replace />;
                          }
                        }

                        if (subscribed && !hasApprovedBankTransfer) {
                          return <Navigate to="/dashboard" replace />;
                        }

                        return (
                          <PrivateRoute>
                            <SubscriptionPage />
                          </PrivateRoute>
                        );
                      })()
                    ))
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />

              <Route
                path="/launch"
                element={
                  <PrivateRoute>
                    <LandingPage />
                  </PrivateRoute>
                }
              />
            </Routes>
          </Suspense>
        )}
      </main>
      {!hideFooter && <Footer />}
      <ScrollToTop />
    </div>
  );
}
export default App;
